import { type FC, useMemo } from "react";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useDemoAgentState } from "../../../hooks/useDemoAgentState";
import { generateDemoData, generateHistoryData } from "../../../utils/mockUtils";
import { type AgentActionHistory, type AgentActionType, AgentActionTypeDisplay } from "../../../utils/models";
import { NotificationsAgent } from "./NotificationsAgent";

interface AgentHistoryProps {
	daysFromNow: number;
	skipInitialLoading?: boolean;
	cachedFutureData?: AgentActionHistory[];
	datePart?: "days" | "hours";
	eligibleNursesCount?: number;
	cacheKey?: string; // Optional cache key for state persistence
	skipAnimation?: boolean; // Skip animation if this message has been rendered before
}

export const DemoAgent: FC<AgentHistoryProps> = ({
	daysFromNow,
	skipInitialLoading = false,
	cachedFutureData,
	datePart = "days",
	eligibleNursesCount = 60,
	cacheKey: providedCacheKey,
	skipAnimation: providedSkipAnimation = false,
}) => {
	const { showSnackbar } = useSnackbar();

	// Use provided cache key or create a unique one based on the component's props
	const cacheKey = useMemo(
		() => providedCacheKey ?? `demo-agent-${daysFromNow}-${datePart}-${eligibleNursesCount}-${cachedFutureData?.length ?? 0}`,
		[providedCacheKey, daysFromNow, datePart, eligibleNursesCount, cachedFutureData?.length],
	);

	const initialFutureData = cachedFutureData ?? generateDemoData(daysFromNow, datePart, eligibleNursesCount);
	const initialHistoryData = generateHistoryData(eligibleNursesCount);

	const { agentHistory, agentFuture, handleEscalate, timelineItems, futureTimelineItems, getCallNurseName, isCalling } = useDemoAgentState({
		cacheKey,
		initialFutureData,
		initialHistoryData,
		skipInitialLoading,
		skipAnimation: providedSkipAnimation,
		onEscalate: (actionType: AgentActionType, count: number, nurseNames?: string[], status?: "loading" | "success" | "error") => {
			const nurseName = nurseNames?.[0];
			if (status === "loading") {
				// Show blue loading notification with nurse name
				showSnackbar(`Calling ${nurseName}...`, "info");
			} else if (status === "error") {
				// Show red error notification when didn't answer
				showSnackbar(`${nurseName} didn't answer`, "error");
			} else {
				// Show success notification for other actions
				showSnackbar(AgentActionTypeDisplay[actionType].successMessage(count, nurseName), "success");
			}
		},
	});

	return (
		<NotificationsAgent
			agentHistory={timelineItems}
			agentFuture={futureTimelineItems}
			isLoadingAgentHistory={agentHistory.isPending}
			isPendingFuture={agentFuture.isPending}
			handleEscalate={handleEscalate}
			getCallNurseName={getCallNurseName}
			isCalling={isCalling}
			skipAnimation={providedSkipAnimation}
		/>
	);
};
